import React from "react";
import { LegalLayout, Section } from "@/components/legal/LegalLayout";
import { Coins, Link as LinkIcon, TrendingUp, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * AffiliateProgram — public landing / policy page for the SociBaba™
 * affiliate & partner program. Explains how creators, publishers and
 * verified sellers earn a commission by referring shoppers to AI Cart™
 * verified checkouts. Sits in `/legal` layout so it inherits the same
 * theme-safe surface used by Privacy, Terms and Refunds.
 */
const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[color:var(--sg-fg)] leading-relaxed">{children}</p>
);

const UL: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="list-disc ml-6 space-y-1.5 text-[color:var(--sg-fg)]">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const Perk: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({
  icon,
  title,
  body,
}) => (
  <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)] p-4 flex items-start gap-3">
    <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="font-display font-semibold text-[color:var(--sg-fg)] tracking-tight text-sm">
        {title}
      </div>
      <div className="text-xs text-[color:var(--sg-muted)] leading-relaxed mt-0.5">
        {body}
      </div>
    </div>
  </div>
);

export const AffiliateProgram: React.FC = () => (
  <LegalLayout
    testId="page-affiliate"
    title="Affiliate Program"
    subtitle="Earn a share every time your audience buys smarter through SociBaba™."
    lastUpdated="February 13, 2026"
    seoPath="/affiliate"
    seoDescription="Join the SociBaba™ Affiliate Program — earn commission on verified AI Cart™ purchases you refer. Transparent tiers, weekly payouts, no lock-in."
    seoKeywords={[
      "SociBaba affiliate program",
      "AI Cart affiliate",
      "creator commissions",
      "product intelligence partner",
      "SibaScore affiliate",
    ]}
  >
    <P>
      SociBaba™ turns any product link into a trusted <b>SibaScore™</b> and a
      cheaper verified seller inside <b>AI Cart™</b>. When you refer shoppers
      to that flow — through your blog, YouTube channel, newsletter, Instagram
      reel or WhatsApp community — you earn a share of every verified purchase
      they make. No pixels required, no exclusivity, no lock-in.
    </P>

    {/* Perks grid */}
    <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="affiliate-perks">
      <Perk
        icon={<Coins className="h-4 w-4 text-[color:var(--sg-primary)]" />}
        title="Up to 12% commission"
        body="Bronze 5%, Silver 8%, Gold 12% — tier climbs with verified referrals."
      />
      <Perk
        icon={<LinkIcon className="h-4 w-4 text-emerald-600" />}
        title="One link, every marketplace"
        body="Your affiliate link routes to AI Cart™ across every major marketplace and verified sellers."
      />
      <Perk
        icon={<TrendingUp className="h-4 w-4 text-fuchsia-600" />}
        title="Weekly payouts"
        body="Every Monday, straight to Razorpay (India) or Stripe (global). ₹500 / $10 minimum."
      />
      <Perk
        icon={<ShieldCheck className="h-4 w-4 text-amber-600" />}
        title="Transparent ledger"
        body="Real-time dashboard shows clicks, verified conversions, tier progress and pending payouts."
      />
    </div>

    <Section n={1} title="Who can join">
      <UL
        items={[
          "Creators & influencers publishing product-related content (YouTube, Instagram, Reels, Shorts, blogs, newsletters).",
          "Deal & coupon publishers, price-tracker communities, comparison sites and shopping newsletters.",
          "Verified SociBaba™ sellers who want to cross-refer their audience to complementary catalogues.",
          "Community leaders running WhatsApp / Telegram / Discord shopping groups.",
        ]}
      />
    </Section>

    <Section n={2} title="How commissions work">
      <UL
        items={[
          "Every affiliate link carries a first-party attribution cookie valid for 30 days.",
          "Commission is paid on the seller-side gross value (excluding GST, shipping and refunds).",
          "Refunds within 14 days automatically reverse the commission. Refunds after 14 days do not.",
          "Bulk / self-purchase abuse is not eligible — SibaScore™ integrity is enforced by the same anti-fraud engine that scores our marketplace.",
        ]}
      />
    </Section>

    <Section n={3} title="Tier ladder">
      <UL
        items={[
          <>
            <b>Bronze</b> — 5% on first ₹50,000 in verified referral GMV. Weekly
            payouts. No prior audience required.
          </>,
          <>
            <b>Silver</b> — 8% once you cross ₹50,000 GMV. Adds early access to
            new AI Cart™ features and a co-branded landing page.
          </>,
          <>
            <b>Gold</b> — 12% once you cross ₹2,00,000 GMV. Adds priority
            support, a dedicated partner manager, and quarterly performance
            bonuses.
          </>,
        ]}
      />
    </Section>

    <Section n={4} title="How to join">
      <P>
        Sign in to your SociBaba™ workspace, visit <b>Settings → Payouts &amp; Affiliate</b>,
        and accept the affiliate terms. Your unique referral link is generated
        instantly. New here?{" "}
        <Link
          to="/signup"
          className="text-[color:var(--sg-primary)] hover:underline"
          data-testid="affiliate-signup-link"
        >
          Create a free workspace
        </Link>{" "}
        first — takes under a minute.
      </P>
    </Section>

    <Section n={5} title="Disclosure & compliance">
      <UL
        items={[
          "Affiliates must clearly disclose paid or commissioned content per ASCI (India), FTC (US) and CMA (UK) guidelines.",
          "Do not run paid ads bidding on the trademarks “SociBaba”, “AI Cart” or “SibaScore”.",
          "Do not misrepresent product SibaScore™ results, tier ranges or verified-seller status.",
          "Repeat violations result in commission reversal and program removal.",
        ]}
      />
    </Section>

    <Section n={6} title="Questions?">
      <P>
        Reach out to{" "}
        <a
          className="text-[color:var(--sg-primary)] hover:underline"
          href="mailto:partners@socibaba.com"
        >
          partners@socibaba.com
        </a>{" "}
        — we reply within one business day.
      </P>
    </Section>

    <div className="mt-10 flex flex-wrap items-center gap-3">
      <Link
        to="/signup"
        data-testid="affiliate-cta-primary"
        className="inline-flex items-center gap-2 rounded-full bg-[color:var(--sg-fg)] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
      >
        <Sparkles className="h-4 w-4" />
        Join the affiliate program
      </Link>
      <Link
        to="/pricing"
        data-testid="affiliate-cta-secondary"
        className="inline-flex items-center gap-2 rounded-full border border-[color:var(--sg-border)] text-[color:var(--sg-fg)] px-5 py-2.5 text-sm font-semibold hover:border-[color:var(--sg-fg)] transition-colors"
      >
        See seller pricing
      </Link>
    </div>
  </LegalLayout>
);

export default AffiliateProgram;
